import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useContext } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { Color } from '@/constants/color';
import AppContext from '@/contexts/appContext';
import { DischargeFormPayload, DischargeFormValues, Props } from '@/forms/DischargeForm/types';
import FieldError from '@/forms/FieldError';
import useDischargeReasons from '@/hooks/useDischargeReasons';
import PersonContext from '@/pages/Person/PersonContext';
import EpisodeOfCareRepository from '@/repositories/EpisodeOfCareRepository/EpisodeOfCareRepository';
import Alert from '@/ui/components/Alert';
import Button from '@/ui/components/Button';
import InputGroup from '@/ui/components/Form/InputGroup';
import Label from '@/ui/components/Form/Label';
import Textarea from '@/ui/components/Form/Textarea';
import Heading from '@/ui/components/Heading';
import Spinner from '@/ui/components/Spinner';
import { currencyFormatter } from '@/utils/currencies';
import formatPersonName from '@/utils/formatPersonName';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';

const DischargeForm = ({ episodeOfCare, onSuccess }: Props) => {
  const { addMessage, currentUser } = useContext(AppContext);
  const {
    person,
    person: { cashout_enabled, id: personId, pending_payout_in_cents }
  } = useContext(PersonContext);
  const queryClient = useQueryClient();
  const episodeOfCareRepository = EpisodeOfCareRepository();
  const { data: dischargeReasons, isPending: isLoadingDischargeReasons } = useDischargeReasons();

  const initialValues: DischargeFormValues = {
    delete_future_appointments: true,
    discharge_reasons: [],
    discharged_by: currentUser?.id,
    note: '',
    remove_from_all_community_circles: true,
    create_payment_for_pending: true
  };

  const {
    control,
    formState: { errors },
    handleSubmit,
    register
  } = useForm({ defaultValues: initialValues });

  const discharge = useMutation({
    mutationFn: ({ values }: { values: DischargeFormPayload }) => {
      return episodeOfCareRepository.discharge({
        params: values,
        episodeOfCareId: episodeOfCare.id
      });
    },
    onSuccess: async () => {
      addMessage({ level: 'success', text: 'Successfully discharged person' });
      personId && (await queryClient.invalidateQueries({ queryKey: ['getPerson', personId] }));
      onSuccess && onSuccess();
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });

  const submitForm = (values: DischargeFormValues) => {
    const payload: DischargeFormPayload = {
      ...values,
      note: values.note ?? ''
    };

    return confirm(
      `Are you sure you want to discharge ${formatPersonName({ person, withNickname: false })}?`
    )
      ? discharge.mutate({ values: payload })
      : null;
  };

  return (
    <div>
      <Heading value={3} text="Why is the member being discharged?" />

      {episodeOfCare.discharged_at && (
        <Alert level="error" message="WARNING: This person has been already been discharged." />
      )}

      {!episodeOfCare.current_active_phase && (
        <Alert
          level="error"
          message="This person does not have an active phase, please add one before trying to discharge."
        />
      )}

      <form onSubmit={handleSubmit(submitForm)} data-testid="discharge-form">
        <InputGroup>
          {isLoadingDischargeReasons ? (
            <Spinner label="Loading discharge reasons" />
          ) : dischargeReasons ? (
            <>
              <Label htmlFor="" text="Discharge Reason">
                {Object.values(dischargeReasons).map((dischargeReason) => {
                  const id = `discharge-reason-${dischargeReason.id}`;

                  return (
                    <div key={id}>
                      <Label htmlFor={id} reverse text={dischargeReason.name}>
                        <input
                          {...register('discharge_reasons', {
                            required: 'At least one discharge reason is required'
                          })}
                          className="mr-1 mb-1"
                          id={id}
                          type="checkbox"
                          value={dischargeReason.id}
                        />
                      </Label>
                    </div>
                  );
                })}
              </Label>
            </>
          ) : null}

          <FieldError message={errors.discharge_reasons?.message} />
        </InputGroup>

        <InputGroup>
          <Label htmlFor="" text="Delete Future Appointments">
            <Label
              htmlFor="id-delete-future-appointments"
              reverse
              text="The member will be removed from all future appointments, and any 1-1 meetings will be cancelled.">
              <input
                {...register('delete_future_appointments')}
                className="mr-1"
                id="id-delete-future-appointments"
                type="checkbox"
              />
            </Label>
          </Label>

          <FieldError message={errors.delete_future_appointments?.message} />
        </InputGroup>

        <InputGroup>
          <Label htmlFor="" text="Remove from all community circles">
            <Label
              htmlFor="id-remove-from-all-community-circles"
              reverse
              text="The member will be removed from all community circles.">
              <input
                {...register('remove_from_all_community_circles')}
                className="mr-1"
                id="id-remove-from-all-community-circles"
                type="checkbox"
              />
            </Label>
          </Label>

          <FieldError message={errors.remove_from_all_community_circles?.message} />
        </InputGroup>

        <InputGroup>
          <Label htmlFor="" text="Create payment for pending line items">
            <Label
              htmlFor="id-create_payment_for_pending"
              reverse
              text="The member will have a payment created for all pending line items.">
              <input
                {...register('create_payment_for_pending')}
                className="mr-1"
                id="id-create_payment_for_pending"
                type="checkbox"
              />
            </Label>
          </Label>

          <FieldError message={errors.create_payment_for_pending?.message} />
          <Alert
            level="info"
            message={
              pending_payout_in_cents
                ? `This person has pending line items with a total of ${currencyFormatter(pending_payout_in_cents / 100)}.`
                : 'This person has no pending line items.'
            }
          />
          {!cashout_enabled && (
            <Alert
              level="warn"
              message="Cashout is not enabled for this person. All payments will be declined."
            />
          )}
        </InputGroup>

        <InputGroup>
          <Controller
            control={control}
            name="note"
            render={({ field }) => {
              return (
                <Textarea
                  id="id-note"
                  labelText="Note"
                  name={field.name}
                  onChange={field.onChange}
                  placeholder="Type any additional info for this discharge"
                />
              );
            }}
          />

          <FieldError message={errors.note?.message} />
        </InputGroup>

        <InputGroup>
          <Button disabled={discharge.isPending} type="submit">
            {discharge.isPending ? <Spinner color={Color.WHITE} label="Submitting..." /> : 'Submit'}
          </Button>
        </InputGroup>
      </form>
    </div>
  );
};

export default DischargeForm;
